import api from '../axios';

/**
 * Serviço para dados de faturas
 */
class FaturasService {
  /**
   * Obtém dados de faturas para uma data específica
   * @param {string} dataRef - Data de referência (formato: YYYY-MM-DDTHH:mm:ss)
   * @returns {Promise} Response da API com dados de faturas
   */
  async getFaturas(dataRef) {
    try {
      const response = await api.get('/api/faturas', {
        params: {
          DataRef: dataRef
        }
      });
      return response;
    } catch (error) {
      console.error('Erro ao buscar faturas:', error);
      throw error;
    }
  }

  /**
   * Obtém dados do equilibrador de faturas para uma data específica
   * @param {string} dataRef - Data de referência (formato: YYYY-MM-DDTHH:mm:ss)
   * @returns {Promise} Response da API com dados do equilibrador
   */
  async getEquilibrador(dataRef) {
    try {
      const response = await api.get('/api/faturas/equilibrador', {
        params: {
          DataRef: dataRef
        }
      });
      return response;
    } catch (error) {
      console.error('Erro ao buscar equilibrador de faturas:', error);
      throw error;
    }
  }

  /**
   * Obtém componentes de faturas para uma data específica
   * @param {string} dataRef - Data de referência (formato: YYYY-MM-DDTHH:mm:ss)
   * @returns {Promise} Response da API com componentes de faturas
   */
  async getComponentes(dataRef) {
    try {
      const response = await api.get('/api/faturas/componentes', {
        params: {
          DataRef: dataRef
        }
      });
      return response;
    } catch (error) {
      console.error('Erro ao buscar componentes de faturas:', error);
      throw error;
    }
  }

  /**
   * Obtém dados de tarifa ANEEL para uma data específica
   * @param {string} dataRef - Data de referência (formato: YYYY-MM-DDTHH:mm:ss)
   * @returns {Promise} Response da API com dados de tarifa ANEEL
   */
  async getTarifaAneel(dataRef) {
    try {
      const response = await api.get('/api/faturas/tarifa-aneel', {
        params: {
          DataRef: dataRef
        }
      });
      return response;
    } catch (error) {
      console.error('Erro ao buscar tarifa ANEEL:', error);
      throw error;
    }
  }

  async obterComparacaoFatura(dataRef) {
    try {
      const response = await api.get('/api/faturas/comparacao', {
        params: {
          dataRef
        }
      });

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Erro ao buscar dados da fatura:', error);
      
      // Fallback com dados mockados quando há erro
      // const dadosFallback = {
      //   dataRef: dataRef,
      //   fatura_convencional: {
      //     titulo: "Fatura Convencional",
      //     valor: 10
      //   },
      //   fatura_hora_certa: {
      //     titulo: "Fatura Hora Certa",
      //     subtitulo: "Valor pago",
      //     valor: 20
      //   },
      //   economia: -10,
      //   resultado: {
      //     tipo: "neutro",
      //     percentual: 0,
      //     mensagem: "PARABÉNS! Você ECONOMIZOU 4% na sua conta de luz em dezembro de 2024 com a Tarifa Hora Certa!",
      //     destaque: "SEM DADOS"
      //   }
      // };

      // return {
      //   success: true,
      //   error: error.message || 'Erro ao carregar dados da fatura',
      //   data: dadosFallback // Fornece dados fallback para evitar quebra da interface
      // };
    }
  }

  /**
   * Busca dados de tarifas detalhadas
   * @param {string} unidadeConsumidora - ID da unidade consumidora
   * @returns {Promise} Dados das tarifas
   */
  async obterTarifasDetalhadas(dataRef) {
    try {
      const response = await api.get('/api/faturas/tarifas-detalhadas', {
        params: {
          dataRef
        }
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Erro ao buscar tarifas detalhadas:', error);
      return {
        success: false,
        error: error.message || 'Erro ao carregar tarifas detalhadas'
      };
    }
  }

  /**
   * Transforma os dados da API no formato esperado pelo componente
   * @param {Object} apiData - Dados vindos da API
   * @returns {Object} Dados formatados para o componente
   */
  formatarDadosFatura(apiData) {
    if (!apiData) return null;

    return {
      dataRef: apiData.mes_referencia || apiData.dataRef,
      dataRefCopel: apiData.dataRefCopel || apiData.data_ref_copel,
      economia: this.formatarValor(apiData.economia || 0),
      faturaConvencional: {
        titulo: apiData.fatura_convencional?.titulo || "Fatura Convencional",
        subtitulo: apiData.fatura_convencional?.subtitulo || "",
        valor: this.formatarValor(apiData.fatura_convencional?.valor || apiData.faturaConvencional?.valor),
        flagFaturaPaga: apiData.fatura_convencional?.flagFaturaPaga || 0
      },
      faturaHoraCerta: {
        titulo: apiData.fatura_hora_certa?.titulo || "Fatura Hora Certa",
        subtitulo: apiData.fatura_hora_certa?.subtitulo || "",
        valor: this.formatarValor(apiData.fatura_hora_certa?.valor || apiData.faturaHoraCerta?.valor),
        flagFaturaPaga: apiData.fatura_hora_certa?.flagFaturaPaga || 0
      },
      resultado: {
        tipo: apiData.resultado?.tipo || (apiData.economia > 0 ? "positivo" : "negativo"),
        percentual: apiData.resultado?.percentual || Math.abs(apiData.economia || 0),
        mensagem: apiData.resultado?.mensagem || this.gerarMensagemResultado(apiData.economia),
        destaque: apiData.resultado?.destaque || (apiData.economia > 0 ? "ECONOMIZOU" : "GASTOU MAIS")
      }
    };
  }

  /**
   * Formatar valor monetário
   * @param {number} valor - Valor numérico
   * @returns {string} Valor formatado como moeda brasileira
   */
  formatarValor(valor) {
    if (!valor) return 'R$ 0,00';
    
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  }

  /**
   * Gerar mensagem de resultado baseada na economia
   * @param {number} economia - Percentual de economia (positivo ou negativo)
   * @returns {string} Mensagem formatada
   */
  gerarMensagemResultado(economia) {
    if (!economia) return 'Valores equivalentes';
    
    if (economia > 0) {
      return `PARABÉNS! Você ECONOMIZOU ${Math.abs(economia)}% na sua conta de luz!`;
    } else {
      return `Com a tarifa Hora Certa você gastaria ${Math.abs(economia)}% a mais na sua conta de luz.`;
    }
  }
  
}

const faturasService = new FaturasService();
export default faturasService;
